import { useCallback, useEffect, useRef, useState } from "react";
import { UrlApi } from "../../../services/apirest";
import { FUENTES, rangoValido } from "./constantes";

// Carga de pedidos por fuente (activos, a facturar, facturados), filtrados por el rango de fechas de registro.
// Activos y a facturar se cargan al entrar; facturados solo cuando se pide (es la lista más grande).
// Al cambiar el rango se recargan las fuentes ya pedidas. Con un rango inválido no se consulta.
export default function usePedidos(rango) {
  const sucursales = localStorage.getItem("suc_auth");
  const [datos, setDatos] = useState({ activos: null, aFacturar: null, facturados: null });
  const [cargando, setCargando] = useState({});
  const [error, setError] = useState(null);
  const [actualizado, setActualizado] = useState(null);
  // Fuentes ya pedidas al menos una vez (para recargarlas)
  const fuentesCargadas = useRef(new Set());
  // Rango vigente, para usarlo desde las funciones memorizadas
  const rangoRef = useRef(rango);
  rangoRef.current = rango;
  // Número de la última consulta por fuente: si el rango cambia rápido, se descartan respuestas viejas
  const ultimaConsulta = useRef({});

  const cargarFuente = useCallback(
    async (fuente) => {
      const rangoActual = rangoRef.current;
      if (!rangoValido(rangoActual)) return;
      fuentesCargadas.current.add(fuente);
      const nro = (ultimaConsulta.current[fuente] || 0) + 1;
      ultimaConsulta.current[fuente] = nro;
      const vigente = () => ultimaConsulta.current[fuente] === nro;

      setCargando((c) => ({ ...c, [fuente]: true }));
      try {
        const respuesta = await fetch(UrlApi + FUENTES[fuente](sucursales, rangoActual));
        const json = await respuesta.json();
        if (!vigente()) return;
        if (!Array.isArray(json)) throw new Error(json?.message || "Respuesta inesperada del servidor");
        setDatos((d) => ({ ...d, [fuente]: json }));
        setActualizado(new Date());
        setError(null);
      } catch (e) {
        if (!vigente()) return;
        console.error(`Error al obtener pedidos (${fuente}):`, e);
        setError("No se pudieron obtener los pedidos. Reintentá con el botón Actualizar.");
      } finally {
        if (vigente()) setCargando((c) => ({ ...c, [fuente]: false }));
      }
    },
    [sucursales]
  );

  // Carga la fuente solo si todavía no se pidió
  const asegurarFuente = useCallback(
    (fuente) => {
      if (!fuentesCargadas.current.has(fuente)) cargarFuente(fuente);
    },
    [cargarFuente]
  );

  // Recarga todas las fuentes que ya se pidieron
  const recargar = useCallback(() => {
    fuentesCargadas.current.forEach((fuente) => cargarFuente(fuente));
  }, [cargarFuente]);

  // Al entrar: activos y a facturar. Al cambiar el rango: recarga lo ya pedido.
  useEffect(() => {
    if (!rangoValido({ desde: rango.desde, hasta: rango.hasta })) return;
    if (!fuentesCargadas.current.size) {
      cargarFuente("activos");
      cargarFuente("aFacturar");
    } else {
      fuentesCargadas.current.forEach((fuente) => cargarFuente(fuente));
    }
  }, [rango.desde, rango.hasta, cargarFuente]);

  return { datos, cargando, error, actualizado, asegurarFuente, recargar };
}
